import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import {
    ISimulation,
    ISimulationCurveGraph,
    Simulation,
    SimulationCurveGraph,
} from '../entities/Simulation/Simulation.ts';

type TherapeuticParams = Pick<
    ISimulation,
    | 'timePercentageInPeriod1'
    | 'responderPercentageInPeriod1'
    | 'timePercentageInPeriod2'
    | 'responderPercentageInPeriod2'
    | 'responderPercentageInSleep'
    | 'timePercentageInSleep'
    | 'rollerCoasterEffectPercentage'
>;

export type Activities = Pick<
    ISimulation,
    'lunchTime' | 'sleepTime' | 'period1Start' | 'period1End' | 'period2Start' | 'period2End'
>;

type AdvancedSettings = Pick<
    ISimulation,
    'morningCMin' | 'morningCMax' | 'afternoonCMin' | 'afternoonCMax' | 'eveningCMin' | 'eveningCMax' | 'threshold'
>;

export interface ISimulationContext {
    weightError: boolean;
    setWeightError: Dispatch<SetStateAction<boolean>>;
    rowsError: boolean;
    setRowsError: Dispatch<SetStateAction<boolean>>;
    name: string;
    setName: Dispatch<SetStateAction<string>>;
    errors: {
        drugId?: string;
        drug?: string;
        dose?: string;
        quantity?: string;
        time?: string;
    }[];
    setErrors: Dispatch<
        SetStateAction<
            {
                drugId?: string;
                drug?: string;
                dose?: string;
                quantity?: string;
                time?: string;
            }[]
        >
    >;
    therapeuticParams: TherapeuticParams | null;
    activities: Activities | null;
    advancedSettings: AdvancedSettings | null;
    weight: number;
    simulationCurveGraph: SimulationCurveGraph | null;
    simulationId: number;
    setTherapeuticParams: Dispatch<SetStateAction<TherapeuticParams | null>>;
    setActivities: Dispatch<SetStateAction<Activities | null>>;
    setAdvancedSettings: Dispatch<SetStateAction<AdvancedSettings | null>>;
    setWeight: Dispatch<SetStateAction<number>>;
    setSimulationCurveGraph: Dispatch<SetStateAction<SimulationCurveGraph | null>>;
    setSimulationId: Dispatch<SetStateAction<number>>;
}

const simulationContext = React.createContext<ISimulationContext>({
    weightError: false,
    errors: [{}],
    rowsError: false,
    name: '',
    therapeuticParams: null,
    activities: null,
    advancedSettings: null,
    weight: 0,
    simulationCurveGraph: null,
    simulationId: 0,
    setTherapeuticParams: () => {},
    setActivities: () => {},
    setAdvancedSettings: () => {},
    setErrors: () => {},
    setWeight: () => {},
    setSimulationCurveGraph: () => {},
    setWeightError: () => {},
    setName: () => {},
    setRowsError: () => {},
    setSimulationId: () => {},
});

type SimulationContextProviderProps = {
    children: React.ReactNode;
};

export const SimulationContextProvider: React.FC<SimulationContextProviderProps> = (props) => {
    const { children } = props;

    const [errors, setErrors] = useState([{}]);
    const [therapeuticParams, setTherapeuticParams] = useState<TherapeuticParams | null>(null);
    const [activities, setActivities] = useState<Activities | null>(null);
    const [advancedSettings, setAdvancedSettings] = useState<AdvancedSettings | null>(null);
    const [weight, setWeight] = useState<number>(0);
    const [simulationCurveGraph, setSimulationCurveGraph] = useState<SimulationCurveGraph | null>(null);
    const [weightError, setWeightError] = useState<boolean>(false);
    const [name, setName] = useState<string>('');
    const [rowsError, setRowsError] = useState<boolean>(false);
    const [simulationId, setSimulationId] = useState(0);

    const contextValue: ISimulationContext = {
        errors,
        setErrors,
        therapeuticParams,
        setTherapeuticParams,
        activities,
        setActivities,
        advancedSettings,
        setAdvancedSettings,
        weight,
        setWeight,
        simulationCurveGraph,
        setSimulationCurveGraph,
        weightError,
        setWeightError,
        name,
        setName,
        rowsError,
        setRowsError,
        simulationId,
        setSimulationId,
    };

    return <simulationContext.Provider value={contextValue}>{children}</simulationContext.Provider>;
};

export function useSimulation() {
    const context: ISimulationContext = useContext(simulationContext);

    const {
        errors,
        setErrors,
        therapeuticParams,
        setTherapeuticParams,
        activities,
        setActivities,
        advancedSettings,
        setAdvancedSettings,
        weight,
        setWeight,
        simulationCurveGraph,
        setSimulationCurveGraph,
        weightError,
        setWeightError,
        name,
        setName,
        rowsError,
        setRowsError,
        simulationId,
        setSimulationId,
    } = context;

    const setSimulationSettingModal = (simulation: Simulation | null) => {
        if (!simulation) 
            return;
        
        const {
            timePercentageInPeriod1,
            responderPercentageInPeriod1,
            timePercentageInPeriod2,
            responderPercentageInPeriod2,
            responderPercentageInSleep,
            timePercentageInSleep,
            rollerCoasterEffectPercentage,
            lunchTime,
            sleepTime,
            period1Start,
            period1End,
            period2Start,
            period2End,
            morningCMin,
            morningCMax,
            afternoonCMin,
            afternoonCMax,
            eveningCMin,
            eveningCMax,
            threshold,
        } = simulation;

        setTherapeuticParams({
            timePercentageInPeriod1,
            responderPercentageInPeriod1,
            timePercentageInPeriod2,
            responderPercentageInPeriod2,
            responderPercentageInSleep,
            timePercentageInSleep,
            rollerCoasterEffectPercentage,
        });

        setActivities({
            lunchTime,
            sleepTime,
            period1Start,
            period1End,
            period2Start,
            period2End,
        });

        setAdvancedSettings({
            morningCMin,
            morningCMax,
            afternoonCMin,
            afternoonCMax,
            eveningCMin,
            eveningCMax,
            threshold,
        });
    };

    const transformCurvesToData = (curves: ISimulationCurveGraph) => {
      if (!curves) return [];
        const numberOfPoints = curves.curve1.length;
        const interval = 1320 / (numberOfPoints - 1); // ≈ 5 min

        const preStartTime = Number(curves.xAxisStart.split(':')[0]) * 60; // 6:00
        const startTime = Number(curves.xAxisCurveStart.split(':')[0]) * 60 + Number(curves.xAxisCurveStart.split(':')[1]);

        const numberOfPrePoints = Math.ceil((startTime - 10 - preStartTime) / interval);

        // 1. Données "null" de 6:00 à 8:00
        const preData = Array.from({ length: numberOfPrePoints }, (_, index) => {
            const currentTime = preStartTime + index * interval;
            const hours = Math.floor(currentTime / 60) % 24;
            const minutes = Math.floor(currentTime % 60);
            return {
                time: roundToNearest5Min(`${hours}:${minutes.toString().padStart(2, '0')}`),
                curve1: null,
                curve2: null,
                curve3: null,
                curve4: null,
                curve5: null,
                curve6: null,
                curve7: null,
                curve8: null,
                curve9: null,
            };
        });

        // 2. Données réelles à partir de 8:00
       const data = Array.from({ length: numberOfPoints - numberOfPrePoints }, (_, index) => {
            const currentTime = startTime + index * interval;
            const hours = Math.floor(currentTime / 60) % 24;
            const minutes = Math.floor(currentTime % 60);
            return {
                time: roundToNearest5Min(`${hours}:${minutes.toString().padStart(2, '0')}`),
                curve1: curves.curve1[index] ?? 0,
                curve2: curves.curve2[index] ?? 0,
                curve3: curves.curve3[index] ?? 0,
                curve4: curves.curve4[index] ?? 0,
                curve5: curves.curve5[index] ?? 0,
                curve6: curves.curve6[index] ?? 0,
                curve7: curves.curve7[index] ?? 0,
                curve8: curves.curve8[index] ?? 0,
                curve9: curves.curve9[index] ?? 0,
            };
        });

        return [...preData, ...data];
    };

    const removeSeconds = (time: string) => {
        return time?.split(':').slice(0, 2).join(':');
    };

    return {
        errors,
        setErrors,
        therapeuticParams,
        setTherapeuticParams,
        activities,
        setActivities,
        advancedSettings,
        setAdvancedSettings,
        setSimulationSettingModal,
        weight,
        setWeight,
        simulationCurveGraph,
        setSimulationCurveGraph,
        transformCurvesToData,
        weightError,
        setWeightError,
        rowsError,
        setRowsError,
        name,
        setName,
        removeSeconds,
        simulationId,
        setSimulationId,
    };
}

export const roundToNearest5Min = (time: string) => {
    let [hours, minutes] = time.split(':').map(Number);
    minutes = Math.round(minutes / 5) * 5;

    if (minutes === 60 || minutes === 55) {
        minutes = 0;
        hours = (hours + 1) % 24;
    }

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const getNearestTime = (targetTime: string, data: { time: string }[]): string | null => {
    if (!targetTime || !data?.length) return null;

    const [targetHours, targetMinutes] = targetTime.split(':').map(Number);
    const targetTotalMinutes = targetHours * 60 + targetMinutes;

    let closest = data[0].time;
    let smallestDiff = Infinity;

    for (const point of data) {
        const [h, m] = point.time.split(':').map(Number);
        const pointMinutes = h * 60 + m;

        const directDiff = Math.abs(pointMinutes - targetTotalMinutes);
        const wrappedDiff = 1440 - directDiff;
        const diff = Math.min(directDiff, wrappedDiff);

        if (diff < smallestDiff) {
            smallestDiff = diff;
            closest = point.time;
        }
    }

    return closest;
};
